import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFileExcel, FaSpinner, FaTable, FaListUl } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { SelectWithSearch, type SelectOption } from '../../utils/select-with-search';

/**
 * User Productivity Report (Work Completed by User)
 * =================================================
 *
 * One row per user for one month: how much survey work they were handed and
 * how much of it they finished. It covers every role — a TA completes reviews,
 * a report maker completes reports — because the question is the same one at
 * each step of the workflow.
 */

interface ReportRow {
  user_id: number;
  user_name: string | null;
  employee_code: string | null;
  role_name: string | null;
  department_name: string | null;
  month_label: string;
  assigned_count: number;
  completed_count: number;
  completion_rate: number | null;
  [key: string]: any;
}

interface Filters {
  user_id: string;
  role_id: string;
  department_id: string;
  month: string;
  year: string;
  /** 'true' keeps users with nothing assigned and nothing completed. */
  include_idle: string;
}

/**
 * One row of the List view: a single piece of work — a user and a site.
 * `work_date` is the site's FIELD WORK START, which is the date a productivity
 * list is read against.
 */
interface ListRow {
  user_id: number;
  user_name: string | null;
  employee_code: string | null;
  role_name: string | null;
  department_name: string | null;
  site_id: number;
  site_code: string | null;
  site_name: string | null;
  project_name: string | null;
  activity_name: string | null;
  work_date: string | null;
  assigned_at: string | null;
  completed_at: string | null;
  completed_stage: string | null;
  was_assigned: boolean;
  was_completed: boolean;
  [key: string]: any;
}

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const now = new Date();

const DEFAULT_FILTERS: Filters = {
  user_id: '',
  role_id: '',
  department_id: '',
  month: String(now.getMonth() + 1),
  year: String(now.getFullYear()),
  include_idle: 'false',
};

/** Ten years back plus the current one — enough for any month a report needs. */
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, index) => now.getFullYear() - index);

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const EMPTY_PAGINATION: NormalizedPagination = {
  total_items: 0,
  current_page: 1,
  total_pages: 1,
  items_per_page: 50,
  has_next_page: false,
  has_previous_page: false,
};

const TABLE_COLUMNS: { key: keyof ReportRow; label: string; numeric?: boolean }[] = [
  { key: 'user_name', label: 'User Name' },
  { key: 'employee_code', label: 'Employee ID' },
  { key: 'role_name', label: 'User Role' },
  { key: 'department_name', label: 'Department' },
  { key: 'month_label', label: 'Month' },
  { key: 'assigned_count', label: 'Assigned', numeric: true },
  { key: 'completed_count', label: 'Completed', numeric: true },
  { key: 'completion_rate', label: 'Completion %', numeric: true },
];

const LIST_COLUMNS: { key: keyof ListRow; label: string; type?: 'date' | 'bool' }[] = [
  { key: 'user_name', label: 'User Name' },
  { key: 'employee_code', label: 'Employee ID' },
  { key: 'role_name', label: 'User Role' },
  { key: 'department_name', label: 'Department' },
  { key: 'site_code', label: 'Site ID' },
  { key: 'site_name', label: 'Site Name' },
  { key: 'project_name', label: 'Project' },
  { key: 'activity_name', label: 'Activity' },
  { key: 'work_date', label: 'Work Date (FW Start)', type: 'date' },
  { key: 'assigned_at', label: 'Assigned On', type: 'date' },
  { key: 'completed_at', label: 'Completed On', type: 'date' },
  { key: 'was_assigned', label: 'Assigned', type: 'bool' },
  { key: 'was_completed', label: 'Completed', type: 'bool' },
];

const formatDate = (value: unknown): string => {
  if (!value) return 'NA';
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const displayList = (row: ListRow, column: { key: keyof ListRow; type?: 'date' | 'bool' }): string => {
  const value = row[column.key];
  if (column.type === 'date') return formatDate(value);
  if (column.type === 'bool') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'NA';
  return String(value);
};

const display = (row: ReportRow, key: keyof ReportRow): string => {
  const value = row[key];
  if (key === 'completion_rate') {
    return value === null || value === undefined ? 'NA' : `${Number(value).toFixed(2)}%`;
  }
  if (value === null || value === undefined || value === '') return 'NA';
  return String(value);
};

const UserProductivityReport: React.FC = () => {
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [userOptions, setUserOptions] = useState<SelectOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<SelectOption[]>([]);
  const [selectedRole, setSelectedRole] = useState<SelectOption | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>(null);

  // 'summary' = one row per user (the counts); 'list' = one row per piece of
  // work behind those counts. Both are loaded by the one Apply Filters button,
  // so switching views never shows a stale or empty half.
  const [viewMode, setViewMode] = useState<'summary' | 'list'>('summary');

  const [draftFilters, setDraftFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(DEFAULT_FILTERS);

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [listRows, setListRows] = useState<ListRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>(EMPTY_PAGINATION);
  const [listPagination, setListPagination] = useState<NormalizedPagination>(EMPTY_PAGINATION);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadFrom = useCallback(
    async <T,>(
      path: string,
      label: string,
      pick: (data: any) => T,
      apply: (value: T) => void,
      params?: Record<string, string | number>,
    ) => {
      try {
        const response = await axiosInstance.get(path, { params });
        if (!response.data?.success) throw new Error(response.data?.message || label);
        apply(pick(response.data.data || {}));
      } catch (error: any) {
        console.error(`[user-productivity] ${path} failed:`, error);
        toast.error(
          error?.response?.data?.message || `Failed to load ${label.toLowerCase()}`,
          { id: `up-filter-${path}` },
        );
      }
    },
    [],
  );

  useEffect(() => {
    loadFrom(
      '/reports/user-productivity/roles',
      'Roles',
      (data) =>
        (data.roles || []).map((role: any) => ({
          id: role.id,
          value: role.id,
          label: role.name,
        })) as SelectOption[],
      setRoleOptions,
    );
    loadFrom(
      '/reports/user-productivity/departments',
      'Departments',
      (data) =>
        (data.departments || []).map((department: any) => ({
          id: department.id,
          value: department.id,
          label: department.name,
        })) as SelectOption[],
      setDepartmentOptions,
    );
  }, [loadFrom]);

  // The user list follows the picked role and department so the filters agree.
  useEffect(() => {
    const params: Record<string, string> = {};
    if (draftFilters.role_id) params.role_id = draftFilters.role_id;
    if (draftFilters.department_id) params.department_id = draftFilters.department_id;
    loadFrom(
      '/reports/user-productivity/users',
      'Users',
      (data) =>
        (data.users || []).map((user: any) => ({
          id: user.id,
          value: user.id,
          label: user.employee_code ? `${user.name} (${user.employee_code})` : user.name,
        })) as SelectOption[],
      setUserOptions,
      Object.keys(params).length > 0 ? params : undefined,
    );
  }, [draftFilters.role_id, draftFilters.department_id, loadFrom]);

  const buildParams = (page: number, limit: number, filters: Filters) => {
    const params: Record<string, string | number> = { page, limit };
    if (filters.user_id) params.user_id = Number(filters.user_id);
    if (filters.role_id) params.role_id = Number(filters.role_id);
    if (filters.department_id) params.department_id = Number(filters.department_id);
    if (filters.month) params.month = Number(filters.month);
    if (filters.year) params.year = Number(filters.year);
    if (filters.include_idle === 'true') params.include_idle = 'true';
    return params;
  };

  const fetchRows = async (
    targetPage = 1,
    targetLimit = pagination.items_per_page,
    filters: Filters = appliedFilters,
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/user-productivity', {
        params: buildParams(targetPage, targetLimit, filters),
      });
      if (!response.data?.success) return;

      const payload = response.data.data || {};
      const data: ReportRow[] = payload.data || [];
      const meta = payload.pagination || {};
      const total = Number(meta.total ?? data.length) || 0;
      const currentPage = Number(meta.current_page ?? targetPage) || targetPage;
      const perPage = Number(meta.per_page ?? targetLimit) || targetLimit;
      const totalPages = Number(meta.total_pages) || Math.max(1, Math.ceil(total / perPage));

      setRows(data);
      setMonthLabel(String(payload.month_label || ''));
      setPagination({
        total_items: total,
        current_page: currentPage,
        total_pages: totalPages,
        items_per_page: perPage,
        has_next_page: Boolean(meta.has_next_page ?? currentPage < totalPages),
        has_previous_page: Boolean(meta.has_prev_page ?? currentPage > 1),
      });
      setHasFetched(true);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to fetch the report');
    } finally {
      setLoading(false);
    }
  };

  const fetchListRows = async (
    targetPage = 1,
    targetLimit = listPagination.items_per_page,
    filters: Filters = appliedFilters,
  ) => {
    try {
      setListLoading(true);
      const response = await axiosInstance.get('/reports/user-productivity/list', {
        params: buildParams(targetPage, targetLimit, filters),
      });
      if (!response.data?.success) return;

      const payload = response.data.data || {};
      const data: ListRow[] = payload.data || [];
      const meta = payload.pagination || {};
      const total = Number(meta.total ?? data.length) || 0;
      const currentPage = Number(meta.current_page ?? targetPage) || targetPage;
      const perPage = Number(meta.per_page ?? targetLimit) || targetLimit;
      const totalPages = Number(meta.total_pages) || Math.max(1, Math.ceil(total / perPage));

      setListRows(data);
      setListPagination({
        total_items: total,
        current_page: currentPage,
        total_pages: totalPages,
        items_per_page: perPage,
        has_next_page: Boolean(meta.has_next_page ?? currentPage < totalPages),
        has_previous_page: Boolean(meta.has_prev_page ?? currentPage > 1),
      });
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to fetch the work list');
    } finally {
      setListLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axiosInstance.get(
        viewMode === 'list'
          ? '/reports/user-productivity/list/export'
          : '/reports/user-productivity/export',
        {
          params: buildParams(1, pagination.items_per_page, appliedFilters),
          responseType: 'blob',
        },
      );

      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fileName = match
        ? decodeURIComponent(match[1])
        : viewMode === 'list'
          ? 'user-productivity-list.xlsx'
          : 'user-productivity.xlsx';

      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (error: any) {
      let message = 'Failed to export the report';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          message = JSON.parse(await data.text())?.message || message;
        } catch {
          /* not JSON — keep the generic message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const paginationAdapter: PaginationType = useMemo(
    () => ({
      offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
      limit: pagination.items_per_page,
      page: pagination.current_page,
      total_pages: pagination.total_pages,
      total_count: pagination.total_items,
      has_next_page: pagination.has_next_page,
      has_prev_page: pagination.has_previous_page,
      next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
      prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
    }),
    [pagination],
  );

  // Totals for the page on screen, so a reader does not have to add up columns.
  const pageTotals = useMemo(
    () =>
      rows.reduce(
        (totals, row) => ({
          assigned: totals.assigned + (Number(row.assigned_count) || 0),
          completed: totals.completed + (Number(row.completed_count) || 0),
        }),
        { assigned: 0, completed: 0 },
      ),
    [rows],
  );

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Both views are loaded together — "Summary and List will be shown after
  // filter", so switching tabs must never be the thing that triggers a fetch.
  const handleApply = () => {
    setAppliedFilters(draftFilters);
    fetchRows(1, pagination.items_per_page, draftFilters);
    fetchListRows(1, listPagination.items_per_page, draftFilters);
  };

  const handleReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setSelectedRole(null);
    setSelectedUser(null);
    setSelectedDepartment(null);
  };

  const listPaginationAdapter: PaginationType = {
    offset: Math.max((listPagination.current_page - 1) * listPagination.items_per_page, 0),
    limit: listPagination.items_per_page,
    page: listPagination.current_page,
    total_pages: listPagination.total_pages,
    total_count: listPagination.total_items,
    has_next_page: listPagination.has_next_page,
    has_prev_page: listPagination.has_previous_page,
    next_page: listPagination.has_next_page ? listPagination.current_page + 1 : null,
    prev_page: listPagination.has_previous_page ? listPagination.current_page - 1 : null,
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              User Productivity Report
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Work assigned to and completed by each user in a month, across every role, on the projects assigned to you.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('summary')}
                aria-pressed={viewMode === 'summary'}
                title="Summary — one row per user"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${viewMode === 'summary' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaTable className="w-4 h-4" /> Summary
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                title="List — one row per site the user worked"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-l border-gray-200 ${viewMode === 'list' ? 'bg-blue-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <FaListUl className="w-4 h-4" /> List
              </button>
            </div>
            <button
              onClick={handleExport}
              disabled={!hasFetched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          <p className="text-sm text-gray-600">
            <strong>Assigned</strong> counts sites handed to the user during the month;{' '}
            <strong>Completed</strong> counts sites the user personally moved into a completion
            stage in it. Both are counted per site, so a site that bounced back and was redone
            still counts once. <strong>Summary</strong> and <strong>List</strong> are both loaded
            when you apply filters — the List names the sites behind the counts, dated by their
            field work start.
          </p>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">User Name</span>
            <SelectWithSearch
              placeholder="All users"
              value={selectedUser}
              options={userOptions}
              onChange={(option) => {
                setSelectedUser(option);
                handleFilterChange('user_id', option ? String(option.value) : '');
              }}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Role</span>
            <SelectWithSearch
              placeholder="All roles"
              value={selectedRole}
              options={roleOptions}
              onChange={(option) => {
                setSelectedRole(option);
                // The user list is scoped to the role, so a user picked under
                // the old one may no longer be in it.
                setSelectedUser(null);
                setDraftFilters((prev) => ({
                  ...prev,
                  role_id: option ? String(option.value) : '',
                  user_id: '',
                }));
              }}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Department Name</span>
            <SelectWithSearch
              placeholder="All departments"
              value={selectedDepartment}
              options={departmentOptions}
              onChange={(option) => {
                setSelectedDepartment(option);
                // The user list is scoped to the department too, so a user
                // picked under the old one may no longer be in it.
                setSelectedUser(null);
                setDraftFilters((prev) => ({
                  ...prev,
                  department_id: option ? String(option.value) : '',
                  user_id: '',
                }));
              }}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Month</span>
            <select
              value={draftFilters.month}
              onChange={(event) => handleFilterChange('month', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Year</span>
            <select
              value={draftFilters.year}
              onChange={(event) => handleFilterChange('year', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              {YEAR_OPTIONS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              checked={draftFilters.include_idle === 'true'}
              onChange={(event) =>
                handleFilterChange('include_idle', event.target.checked ? 'true' : 'false')
              }
              className="w-4 h-4 rounded border-gray-300"
            />
            <span className="text-xs font-medium text-gray-700">
              Include users with no work this month
            </span>
          </label>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={handleReset}
            className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-300 disabled:cursor-not-allowed"
          >
            {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : null}
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
        </div>
      </div>

      <div
        className="bg-white shadow-sm rounded overflow-hidden border border-gray-200"
        hidden={viewMode !== 'summary'}
      >
        {hasFetched && (
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-700">{monthLabel}</p>
            <p className="text-sm text-gray-500">{pagination.total_items} user(s)</p>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th
                    key={String(column.key)}
                    className={`px-4 py-3 text-xs font-semibold text-gray-600 whitespace-nowrap ${
                      column.numeric ? 'text-right' : 'text-left'
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    Loading...
                  </td>
                </tr>
              ) : !hasFetched ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    Apply filters to load the report.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    No users worked on anything in this month.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.user_id} className="hover:bg-gray-50">
                    {TABLE_COLUMNS.map((column) => (
                      <td
                        key={String(column.key)}
                        className={`px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap ${
                          column.numeric ? 'text-right' : 'text-left'
                        }`}
                      >
                        {display(row, column.key)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {!loading && rows.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td
                    colSpan={5}
                    className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap"
                  >
                    Page total
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                    {pageTotals.assigned}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                    {pageTotals.completed}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                    {pageTotals.assigned > 0
                      ? `${((pageTotals.completed / pageTotals.assigned) * 100).toFixed(2)}%`
                      : 'NA'}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {hasFetched && (
          <Pagination
            pagination={paginationAdapter}
            onPageChange={(nextPage) => fetchRows(nextPage, pagination.items_per_page, appliedFilters)}
            onLimitChange={(nextLimit) => fetchRows(1, nextLimit, appliedFilters)}
          />
        )}
      </div>

      {/* List view — the work behind the counts, one row per user x site. */}
      <div
        className="bg-white shadow-sm rounded overflow-hidden border border-gray-200"
        hidden={viewMode !== 'list'}
      >
        {hasFetched && (
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-700">{monthLabel}</p>
            <p className="text-sm text-gray-500">{listPagination.total_items} work item(s)</p>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {LIST_COLUMNS.map((column) => (
                  <th
                    key={String(column.key)}
                    className="px-4 py-3 text-xs font-semibold text-gray-600 whitespace-nowrap text-left"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listLoading ? (
                <tr>
                  <td colSpan={LIST_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    Loading...
                  </td>
                </tr>
              ) : !hasFetched ? (
                <tr>
                  <td colSpan={LIST_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    Apply filters to load the report.
                  </td>
                </tr>
              ) : listRows.length === 0 ? (
                <tr>
                  <td colSpan={LIST_COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-600">
                    No work was assigned or completed in this month.
                  </td>
                </tr>
              ) : (
                listRows.map((row) => (
                  <tr key={`${row.user_id}-${row.site_id}`} className="hover:bg-gray-50">
                    {LIST_COLUMNS.map((column) => (
                      <td
                        key={String(column.key)}
                        className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap"
                      >
                        {displayList(row, column)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasFetched && (
          <Pagination
            pagination={listPaginationAdapter}
            onPageChange={(nextPage) =>
              fetchListRows(nextPage, listPagination.items_per_page, appliedFilters)
            }
            onLimitChange={(nextLimit) => fetchListRows(1, nextLimit, appliedFilters)}
          />
        )}
      </div>
    </div>
  );
};

export default UserProductivityReport;
